/**
 * Field readers for invitation / accept-invitation responses.
 *
 * GET /invitations/{token} returns:
 *   `{ status, file_id, file_title, highlight_chapter_id,
 *     highlight_chapter_title, sharer_username, recipient_email,
 *     recipient_account_exists, expires_at }`
 *
 * POST /invitations/{token}/accept returns:
 *   `{ share_id, file_id, highlight_chapter_id, status }`
 *
 * All shares are book-scoped; chapter selection is expressed via the
 * optional `highlight_chapter_id` for deep-linking on first open.
 */

export function pickSharerName(invitation) {
  if (!invitation) return 'Someone';
  return (
    invitation.sharer_username ||
    invitation.sharer_name ||
    invitation.sharer?.username ||
    invitation.sharer?.name ||
    invitation.sharer?.email ||
    invitation.shared_by ||
    'Someone'
  );
}

export function pickContentTitle(invitation) {
  if (!invitation) return 'Shared content';
  return (
    invitation.file_title ||
    invitation.title ||
    invitation.book_title ||
    invitation.content_title ||
    invitation.file?.title ||
    'Shared content'
  );
}

export function pickChapterLabel(invitation) {
  if (!invitation) return null;
  const chapterTitle =
    invitation.highlight_chapter_title ||
    invitation.chapter_title ||
    invitation.chapter?.title ||
    null;
  const chapterOrder =
    invitation.chapter_order != null ? invitation.chapter_order : invitation.chapter?.order;
  if (chapterTitle && chapterOrder != null) {
    return `Chapter ${chapterOrder}: ${chapterTitle}`;
  }
  if (chapterTitle) return chapterTitle;
  if (chapterOrder != null) return `Chapter ${chapterOrder}`;
  return null;
}

export function pickPersonalMessage(invitation) {
  if (!invitation) return null;
  const msg = invitation.personal_message || invitation.message || null;
  return msg && String(msg).trim() ? String(msg).trim() : null;
}

export function pickFileId(source) {
  if (!source) return null;
  return (
    source.file_id ||
    source.book_id ||
    source.content?.file_id ||
    source.file?.id ||
    null
  );
}

export function pickChapterId(source) {
  if (!source) return null;
  return (
    source.highlight_chapter_id ||
    source.chapter_id ||
    source.chapter?.id ||
    null
  );
}

export function isAlreadyAccepted(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const detail = payload.detail || payload.message || '';
  if (typeof detail === 'string') {
    return /already\s+accepted/i.test(detail);
  }
  return false;
}

/**
 * Build a navigation target for redirecting after a successful accept (or for
 * an "already accepted" recovery). When a `highlight_chapter_id` is present
 * on either the accept result or the original invitation, the link includes
 * `?highlight={chapterId}`. Falls back to `/dashboard` when no file id can
 * be determined.
 */
export function buildContentRedirect(acceptResult, invitation) {
  const fileId = pickFileId(acceptResult) || pickFileId(invitation);
  if (!fileId) return '/dashboard';
  const chapterId = pickChapterId(acceptResult) || pickChapterId(invitation);
  if (chapterId) {
    return `/projects/${fileId}/chapters?highlight=${encodeURIComponent(chapterId)}`;
  }
  return `/projects/${fileId}/chapters`;
}
